/**
 * TD-28 段階④: 申請フォームの条件付き表示/必須・計算式のクライアント側評価 (表示専用のミラー)。
 *
 * ⚠️ サーバー (apps/api/src/lib/request-forms/ast.ts) が唯一の正である。ここはユーザーが入力する
 * そばから項目の出し分けと計算結果を見せるための描画用実装で、提出時にはサーバーが同じ AST を
 * 評価し直し、値を再計算・再検証して保存する (is_computed フィールドのクライアント値は
 * サーバー側で必ず破棄される)。したがってこの実装が壊れても保存されるデータは壊れない。
 *
 * サーバー実装と同じ語彙・同じ意味論を保つこと (ノード種別の有限リスト、if の遅延評価、
 * 型不一致は例外にせず「評価不能」として扱う)。
 */

export type AstNode = Record<string, unknown>;
export type AstValue = string | number | boolean | null;

const MAX_DEPTH = 12;
// サーバー (apps/api/src/lib/request-forms/ast.ts) の AST_MAX_NODES と同じ値にすること
const MAX_NODES = 80;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

class EvalError extends Error {}

const num = (v: AstValue): number => {
  if (typeof v === 'number') return v;
  throw new EvalError('number required');
};
const bool = (v: AstValue): boolean => {
  if (typeof v === 'boolean') return v;
  throw new EvalError('boolean required');
};
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** 使い勝手フィードバック⑥ (D-U28): request-form-validation.ts からも再利用する (§8-55 対策)。 */
export const isRealDate = (s: string): boolean => {
  if (!DATE_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d;
};
const dateStr = (v: AstValue): string => {
  if (typeof v === 'string' && isRealDate(v)) return v;
  throw new EvalError('date required');
};

type EvalCtx = { nodeCount: number };

const evalNode = (node: unknown, values: Map<string, AstValue>, depth: number, ctx: EvalCtx): AstValue => {
  if (depth > MAX_DEPTH) throw new EvalError('too deep');
  ctx.nodeCount += 1;
  if (ctx.nodeCount > MAX_NODES) throw new EvalError('too many nodes');
  if (!isPlainObject(node)) throw new EvalError('invalid node');
  const op = node.op;
  const l = () => evalNode(node.left, values, depth + 1, ctx);
  const r = () => evalNode(node.right, values, depth + 1, ctx);
  switch (op) {
    case 'literal': {
      const v = node.value;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
      throw new EvalError('invalid literal');
    }
    case 'field_ref': {
      const id = node.field_definition_id;
      if (typeof id !== 'string') throw new EvalError('invalid field_ref');
      // ⚠️ Map で解決する (`values[id]` 形式の動的プロパティアクセスは書かない、サーバーと同じ方針)
      return values.has(id) ? values.get(id)! : null;
    }
    case 'add': return num(l()) + num(r());
    case 'sub': return num(l()) - num(r());
    case 'mul': return num(l()) * num(r());
    case 'div': {
      const right = num(r());
      if (right === 0) throw new EvalError('divide by zero');
      return num(l()) / right;
    }
    case 'eq': return l() === r();
    case 'neq': return l() !== r();
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const a = l(); const b = r();
      let cmp: number;
      if (typeof a === 'number' && typeof b === 'number') cmp = a - b;
      else if (typeof a === 'string' && typeof b === 'string') cmp = a < b ? -1 : a > b ? 1 : 0;
      else throw new EvalError('type mismatch');
      if (op === 'gt') return cmp > 0;
      if (op === 'gte') return cmp >= 0;
      if (op === 'lt') return cmp < 0;
      return cmp <= 0;
    }
    case 'and': return bool(l()) && bool(r());
    case 'or': return bool(l()) || bool(r());
    case 'not': return !bool(evalNode(node.value, values, depth + 1, ctx));
    case 'date_diff_days': {
      const start = dateStr(evalNode(node.start, values, depth + 1, ctx));
      const end = dateStr(evalNode(node.end, values, depth + 1, ctx));
      return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000);
    }
    case 'if': {
      // ⚠️ 選択されなかった分岐は評価しない (サーバーと同じ遅延評価)
      const cond = bool(evalNode(node.cond, values, depth + 1, ctx));
      return evalNode(cond ? node.then : node.else, values, depth + 1, ctx);
    }
    default:
      throw new EvalError(`unknown op: ${String(op)}`);
  }
};

/** 評価できなければ null を返す (例外を投げない)。表示用のため fail-safe に倒す。 */
export const evaluateAstSafe = (node: unknown, values: Map<string, AstValue>): AstValue => {
  try {
    return evalNode(node, values, 0, { nodeCount: 0 });
  } catch {
    return null;
  }
};
